/* /assets/section-funnel-pricing.js
   Detects an active product discount (URL param or discount_code cookie) and
   updates the displayed price + Save badge + banner ONLY on the offer cards
   whose discount code matches. Each offer card declares its own code + value,
   so a single product discount hits a single offer (e.g. −100€ on the middle
   bundle) while the other cards stay untouched. */
(function () {
  'use strict';

  function init() {
    document.querySelectorAll('[data-funnel-pricing]').forEach(applyDiscountToSection);
  }

  function applyDiscountToSection(section) {
    if (section.dataset.dynamicDiscount !== 'true') return;

    var paramName = section.dataset.discountParam || 'promo';
    var savingsTpl = section.dataset.savingsTemplate || 'Save {pct}%';
    var msgTpl = section.dataset.bannerTemplate || '';
    var display = section.dataset.discountDisplay || 'card';

    var activeCode = detectActiveCode(paramName);
    if (!activeCode) return;
    var activeKey = activeCode.toUpperCase();

    var applied = null;
    section.querySelectorAll('[data-fp-card]').forEach(function (card) {
      var match = findCardDiscount(card, activeKey);
      if (!match) return;
      // carry the discount into checkout: append ?discount=CODE to this offer's CTA
      appendDiscountToCta(card, match.code);
      var result = updateCardPricing(card, match, savingsTpl);
      if (!result) return;
      if (!applied) applied = result;
      if (display === 'card' || display === 'both') {
        fillDiscountText(card.querySelector('[data-fp-card-discount]'), msgTpl, result);
      }
    });

    if (applied && (display === 'banner' || display === 'both')) {
      showBanner(section, applied, msgTpl);
    }
  }

  // Append the active discount code to an offer's checkout button so it carries
  // into checkout. Only runs for the offer whose code is active; other offers
  // (and visitors without the discount) keep their plain link.
  function appendDiscountToCta(card, code) {
    var cta = card.querySelector('.funnel-pricing__cta');
    if (!cta) return;
    var href = cta.getAttribute('href');
    if (!href || href === '#') return;
    if (/[?&]discount=/i.test(href)) return; // already has a discount param
    var sep = href.indexOf('?') === -1 ? '?' : '&';
    cta.setAttribute('href', href + sep + 'discount=' + encodeURIComponent(code));
  }

  // Collect every discount code a card declares: the primary code/type/value
  // fields plus the optional "extra codes" list (one per line, CODE=VALUE,
  // trailing % = percent). Returns the entry matching the active code, or null.
  function findCardDiscount(card, activeKey) {
    var entries = [];
    var primary = (card.dataset.discountCode || '').trim();
    if (primary) {
      entries.push({
        code: primary,
        type: (card.dataset.discountType || 'fixed').toLowerCase(),
        value: parseNumber(card.dataset.discountValue),
      });
    }
    var extra = card.dataset.discountCodes || '';
    extra.split(/[\n,;]+/).forEach(function (line) {
      var parts = line.split(/[=:]/);
      if (parts.length < 2) return;
      var code = parts[0].trim();
      var val = parts.slice(1).join('').trim();
      if (!code || !val) return;
      var isPct = /%\s*$/.test(val);
      entries.push({
        code: code,
        type: isPct ? 'percent' : 'fixed',
        value: parseNumber(val),
      });
    });
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].code.toUpperCase() === activeKey) return entries[i];
    }
    return null;
  }

  function detectActiveCode(paramName) {
    // 1. URL param wins — also carried by the redirect marker in the discount link
    var params = new URLSearchParams(window.location.search);
    var raw = params.get(paramName);
    if (raw != null && String(raw).trim() !== '') return String(raw).trim();

    // 2. discount_code cookie set by Shopify after visiting /discount/CODE
    var cookie = getCookie('discount_code');
    if (cookie) return cookie.trim();

    return '';
  }

  function updateCardPricing(card, discount, savingsTpl) {
    var priceEl = card.querySelector('[data-fp-price]');
    if (!priceEl) return null;

    var origFmt = parsePrice(priceEl.dataset.originalPrice || priceEl.textContent);
    if (!origFmt) return null;

    var code = discount.code;
    var type = discount.type || 'fixed';
    var value = discount.value;
    if (!(value > 0)) return null;

    var newPriceNum;
    if (type === 'percent') {
      newPriceNum = origFmt.num * (1 - value / 100);
    } else {
      newPriceNum = origFmt.num - value;
    }
    newPriceNum = roundTo(newPriceNum, 2);
    if (newPriceNum < 0) newPriceNum = 0;

    var reduction = roundTo(origFmt.num - newPriceNum, 2);
    if (reduction <= 0) return null;

    priceEl.textContent = formatPrice(newPriceNum, origFmt);

    var savingsEl = card.querySelector('[data-fp-savings]');
    var compareEl = card.querySelector('[data-fp-compare-price]');
    if (savingsEl && compareEl) {
      var compareFmt = parsePrice(compareEl.dataset.fpComparePrice || compareEl.textContent);
      if (compareFmt && compareFmt.num > newPriceNum) {
        var savingsPct = Math.round(((compareFmt.num - newPriceNum) / compareFmt.num) * 100);
        savingsEl.textContent = savingsTpl.replace('{pct}', String(savingsPct));
        savingsEl.hidden = false;
      }
    }

    card.classList.add('is-discounted');

    var pctOff = origFmt.num > 0 ? Math.round((reduction / origFmt.num) * 100) : 0;
    return {
      code: code,
      amount: formatPrice(reduction, origFmt),
      pct: pctOff,
      value: value,
    };
  }

  function renderTpl(tpl, info) {
    return tpl
      .replace('{pct}', String(info.pct))
      .replace('{amount}', info.amount)
      .replace('{value}', String(info.value))
      .replace('{code}', info.code || '');
  }

  function fillDiscountText(wrap, tpl, info) {
    if (!wrap || !tpl) return;
    var textEl = wrap.querySelector('[data-fp-card-discount-text]') || wrap;
    textEl.textContent = renderTpl(tpl, info);
    wrap.hidden = false;
  }

  function showBanner(section, applied, tpl) {
    var banner = section.querySelector('[data-discount-banner]');
    if (!banner || !tpl) return;
    var textEl = banner.querySelector('[data-discount-banner-text]') || banner;
    textEl.textContent = renderTpl(tpl, applied);
    banner.hidden = false;
  }

  /* ---------- helpers ---------- */

  function parseNumber(str) {
    if (str == null) return NaN;
    var clean = String(str).replace(/[^\d.,-]/g, '').trim();
    if (clean === '') return NaN;
    var lastComma = clean.lastIndexOf(',');
    var lastDot = clean.lastIndexOf('.');
    if (lastComma > -1 && lastComma > lastDot) {
      // EU decimal comma, e.g. 1.234,56 or 100,50
      clean = clean.replace(/\./g, '').replace(',', '.');
    } else {
      clean = clean.replace(/,/g, '');
    }
    return parseFloat(clean);
  }

  function parsePrice(str) {
    if (!str) return null;
    var trimmed = String(str).trim();
    // Split into: optional prefix non-digit chars + number + optional suffix non-digit chars
    var m = trimmed.match(/^([^\d\-]*)\s*(-?[\d.,]+)\s*([^\d]*)$/);
    if (!m) return null;
    var prefix = m[1] || '';
    var suffix = m[3] || '';
    var raw = m[2];

    var lastComma = raw.lastIndexOf(',');
    var lastDot = raw.lastIndexOf('.');
    var commaDecimal = false;
    var clean;

    if (lastComma > -1 && lastDot > -1) {
      if (lastComma > lastDot) {
        // EU: 1.234,56
        clean = raw.replace(/\./g, '').replace(',', '.');
        commaDecimal = true;
      } else {
        // US: 1,234.56
        clean = raw.replace(/,/g, '');
      }
    } else if (lastComma > -1) {
      var afterComma = raw.length - lastComma - 1;
      if (afterComma <= 2) {
        clean = raw.replace(',', '.');
        commaDecimal = true;
      } else {
        clean = raw.replace(/,/g, '');
      }
    } else {
      clean = raw;
    }

    var num = parseFloat(clean);
    if (isNaN(num)) return null;

    var decPart = clean.split('.')[1];
    var decimals = decPart ? decPart.length : 0;

    return {
      num: num,
      prefix: prefix,
      suffix: suffix,
      decimals: decimals,
      commaDecimal: commaDecimal,
    };
  }

  function formatPrice(num, fmt) {
    var dec = fmt.decimals;
    if (dec === 0 && num % 1 !== 0) dec = 2;
    var str = num.toFixed(dec);
    if (fmt.commaDecimal) str = str.replace('.', ',');
    return fmt.prefix + str + fmt.suffix;
  }

  function roundTo(num, decimals) {
    var factor = Math.pow(10, decimals);
    return Math.round(num * factor) / factor;
  }

  function getCookie(name) {
    var parts = ('; ' + document.cookie).split('; ' + name + '=');
    if (parts.length !== 2) return '';
    return decodeURIComponent(parts.pop().split(';').shift());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init in theme editor when sections are loaded
  document.addEventListener('shopify:section:load', function (e) {
    var sec = e.target.querySelector('[data-funnel-pricing]') || (e.target.matches && e.target.matches('[data-funnel-pricing]') ? e.target : null);
    if (sec) applyDiscountToSection(sec);
  });
})();
